import React, { useContext, useCallback, FunctionComponent, useState, useRef, ChangeEvent } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { UPSERT_UCCM_ANNOUNCEMENT_MLS, DELETE_ANNOUNCEMENT_MLS } from "src/api/featureAnnouncement";
import { UpsertAnnouncementsMls, UpsertAnnouncementsMlsVariables } from "src/api/generated/UpsertAnnouncementsMls";
import { DeleteAnnouncementMls, DeleteAnnouncementMlsVariables } from "src/api/generated/DeleteAnnouncementMls";
import { MessageType } from "src/components/notifications/Notifier";
import { getRoleKey, Role as CustomerRole } from "src/network/User";
import { GetFeatureAnnouncementContentDetails_announcementcontent as announcementcontent } from "src/api/generated/GetFeatureAnnouncementContentDetails";
import styles from "./FeatureAnnouncementContentDetailsForm.module.css";
import { selectRoute, Route } from "src/config/routes";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { FORM_STATUS, useRegisterRef } from "src/utils/Form";
import { useSaveUnsavedChangesInForm } from "src/hooks/save-unsaved-changes-in-form/useSaveUnsavedChangesInForm";
import { useSaveFormByShortcut } from "src/hooks/form/useSaveFormByShortcut";
import { ColorStyle } from "src/utils/Colors";
import { getEnvironment } from "src/utils/Customer";
import { NetworkConfig } from "src/network/NetworkConfig";

type FeatureAnnouncementContentDetailsFormProps = {
    featureAnnouncementContentDetails?: announcementcontent;
    announcementId: string;
    contentId: string;
    onUpsertFeatureAnnouncementContent: any;
    userRole?: CustomerRole;
};

interface FeatureAnnouncementContentDetailsFormFields {
    id: string;
    language: string;
    teaser: string;
    title: string;
    subtitle: string;
    content: string;
}

interface MediaFile {
    fileName: string;
    fileId: string;
    fileExtension: string;
    fileUrl: string;
}

interface FileHTMLInputEvent extends ChangeEvent {
    target: HTMLInputElement & EventTarget;
}

const CONTENT_TEXT_PLACEHOLDER = `Hier Text als HTML Eingeben. 
Bsp.:
<p>Erster Paragraph</p>
<p>Zweiter Paragraph</p>

Medien-Elemente via 'Medien hochladen' + Kopieren Button einfügen`;

const DEFAULT_ANNOUNCEMENT_LANGUAGE = "de";

const FeatureAnnouncementContentDetailsFormBase: FunctionComponent<FeatureAnnouncementContentDetailsFormProps> = (
    props
) => {
    const { uiStore, authStore } = useContext(RootStoreContext);
    const history = useHistory();

    const [isNewFeatureAnnouncementContent, setIsNewFeatureAnnouncementContent] = useState<boolean>(
        props.featureAnnouncementContentDetails ? false : true
    );
    const [currentStatus, setCurrentStatus] = useState<FORM_STATUS>(FORM_STATUS.NONE);
    const [isUploadingMediaFile, setIsUploadingMediaFile] = useState<boolean>(false);

    const {
        announcementId,
        contentId,
        featureAnnouncementContentDetails,
        userRole,
        onUpsertFeatureAnnouncementContent
    } = props;

    const [upsertUccmAnnouncementMlsMutation] = useMutation<UpsertAnnouncementsMls, UpsertAnnouncementsMlsVariables>(
        UPSERT_UCCM_ANNOUNCEMENT_MLS,
        {
            onCompleted: () => {
                uiStore.printStatusMessage("Content wurde erfolgreich gespeichert", MessageType.INFO);
            }
        }
    );

    const [deleteAnnouncementMlsMutation] = useMutation<DeleteAnnouncementMls, DeleteAnnouncementMlsVariables>(
        DELETE_ANNOUNCEMENT_MLS,
        {
            onCompleted: () => {
                uiStore.printStatusMessage("Content wurde erfolgreich gelöscht", MessageType.INFO);
            }
        }
    );

    const getTitle = useCallback((content: string): string => {
        const titleElements = /<h1>(.*?)<\/h1>/g.exec(content ?? "");
        const title = titleElements && titleElements.length > 0 ? titleElements[1] : "";

        return title;
    }, []);

    const getSubtitle = useCallback((content: string): string => {
        const subtitleElements = /<h2>(.*?)<\/h2>/g.exec(content ?? "");
        const subtitle = subtitleElements && subtitleElements.length > 0 ? subtitleElements[1] : "";

        return subtitle;
    }, []);

    const getContent = useCallback((fullContent: string): string => {
        const content = fullContent.replace(/<div>|<\/div>|<h1>.*?<\/h1>|<h2>.*?<\/h2>/gim, "").trim();

        return content;
    }, []);

    const { register, handleSubmit, formState, reset, errors, watch } =
        useForm<FeatureAnnouncementContentDetailsFormFields>({
            mode: "onBlur",
            defaultValues: {
                id: contentId,
                language: featureAnnouncementContentDetails?.language ?? DEFAULT_ANNOUNCEMENT_LANGUAGE,
                teaser: featureAnnouncementContentDetails?.teaser ?? "",
                title: getTitle(featureAnnouncementContentDetails?.content ?? ""),
                subtitle: getSubtitle(featureAnnouncementContentDetails?.content ?? ""),
                content: getContent(featureAnnouncementContentDetails?.content ?? "")
            }
        });
    const registerRef = useRegisterRef(register);

    const { dirtyFields, isDirty } = formState;

    const watchLanguage = watch("language");

    const [mediaFiles, setMediaFiles] = useState<Array<MediaFile>>(
        props.featureAnnouncementContentDetails &&
            props.featureAnnouncementContentDetails.announcement.announcementfiles
            ? props.featureAnnouncementContentDetails.announcement.announcementfiles
                  .filter((file) => file.language === watchLanguage)
                  .map((file) => {
                      return {
                          fileId: file.id,
                          fileName: file.name,
                          fileExtension: file.extension,
                          fileUrl: `https://${getEnvironment()}.wwportal.ch/files/announcements/${announcementId}/${watchLanguage}/${
                              file.id
                          }.${file.extension}`
                      };
                  })
            : []
    );

    const refSubmitButtom = useRef<HTMLButtonElement>(null);
    const hiddenMediaInputButton = useRef<HTMLInputElement>(null);

    const saveForm = useCallback(() => {
        refSubmitButtom?.current?.click();
    }, []);

    useSaveFormByShortcut({ saveFormFunction: saveForm, isFormDirty: isDirty });

    const { saveFormModal, closeSaveFormModalAndGoBack } = useSaveUnsavedChangesInForm({
        saveFormFunction: saveForm,
        currentFormStatus: currentStatus,
        isFormDirty: isDirty
    });

    const resetForm = useCallback(
        (e: any) => {
            e.preventDefault();

            if (featureAnnouncementContentDetails) {
                reset({
                    id: contentId,
                    language: featureAnnouncementContentDetails?.language ?? DEFAULT_ANNOUNCEMENT_LANGUAGE,
                    teaser: featureAnnouncementContentDetails?.teaser ?? "",
                    title: getTitle(featureAnnouncementContentDetails?.content ?? ""),
                    subtitle: getSubtitle(featureAnnouncementContentDetails?.content ?? ""),
                    content: getContent(featureAnnouncementContentDetails?.content ?? "")
                });
            } else {
                reset({
                    id: contentId,
                    language: DEFAULT_ANNOUNCEMENT_LANGUAGE,
                    teaser: "",
                    title: "",
                    subtitle: "",
                    content: ""
                });
            }
        },
        [contentId, featureAnnouncementContentDetails, getContent, getSubtitle, getTitle, reset]
    );

    const handleFormSubmit = useCallback(
        async (formData: FeatureAnnouncementContentDetailsFormFields) => {
            setCurrentStatus(FORM_STATUS.SAVING_FORM);

            const content = `<div>\r\n<h1>${formData.title}</h1>\r\n<h2>${formData.subtitle}</h2>\r\n${formData.content}\r\n</div>`;

            const updatedFeatureAnnouncementMlsDetails: any = {
                id: formData.id,
                announcementid: announcementId,
                language: formData.language ?? watchLanguage,
                teaser: formData.teaser,
                content: content
            };

            try {
                await upsertUccmAnnouncementMlsMutation({
                    variables: {
                        announcementsmls: updatedFeatureAnnouncementMlsDetails
                    }
                });

                onUpsertFeatureAnnouncementContent();

                if (isNewFeatureAnnouncementContent) {
                    uiStore.setHasNewAnnouncementContentData(true);
                    setIsNewFeatureAnnouncementContent(false);
                }

                reset(
                    {
                        ...formData
                    },
                    {
                        isDirty: false,
                        dirtyFields: false
                    }
                );
            } catch (error) {
                uiStore.printStatusMessage(`Fehler beim Speichern des Contents: ${error.message}`, MessageType.ERROR);
            } finally {
                setCurrentStatus(FORM_STATUS.NONE);
                closeSaveFormModalAndGoBack();
            }
        },
        [
            announcementId,
            closeSaveFormModalAndGoBack,
            isNewFeatureAnnouncementContent,
            onUpsertFeatureAnnouncementContent,
            reset,
            uiStore,
            upsertUccmAnnouncementMlsMutation,
            watchLanguage
        ]
    );

    const isImage = useCallback((extension: string) => {
        if (extension === "jpg" || extension === "jpeg" || extension === "gif" || extension === "png") {
            return true;
        } else {
            return false;
        }
    }, []);

    const onCopyMedia = useCallback(
        (fileId: string, extension: string) => () => {
            let mediaUrl = `https://${getEnvironment()}.wwportal.ch/files/announcements/${announcementId}/${watchLanguage}/${fileId}.${extension}`;
            let copyElementText = "";

            if (isImage(extension)) {
                copyElementText = `<img onclick="function openInNewWindow(url){window.open(url, '_blank');};openInNewWindow(this.src);" src="${mediaUrl}" />`;
            } else {
                copyElementText = `<video width="100%" height="auto" controls><source src="${mediaUrl}" type="video/mp4">Your browser does not support the video tag.</video>`;
            }

            const elem = document.createElement("textarea");
            elem.value = copyElementText;
            document.body.appendChild(elem);
            elem.select();
            document.execCommand("copy");
            document.body.removeChild(elem);

            uiStore.printStatusMessage("HTML-Medien-Element in Zwischenablage gespeichert", MessageType.INFO);
        },
        [announcementId, uiStore, isImage, watchLanguage]
    );

    const onDeleteMedia = useCallback(
        (fileId: string) => async () => {
            if (authStore.user && authStore.token) {
                const accessToken = authStore.token;
                const tokenType = authStore.tokenType;
                const role = authStore.user?.role;

                try {
                    const fetchResult = await fetch(
                        `${NetworkConfig.deleteAnnouncementFileUrl}/${fileId}?lang=${watchLanguage}`,
                        {
                            method: "PUT",
                            body: JSON.stringify({}),
                            headers: {
                                "Content-type": "multipart/form-data",
                                Authorization: `${tokenType} ${accessToken}`,
                                "x-hasura-role": getRoleKey(role)
                            }
                        }
                    );

                    if (fetchResult.status === 200) {
                        uiStore.printStatusMessage("Datei erfolgreich gelöscht", MessageType.INFO);
                        const newMediaFilesArray = mediaFiles.filter((file) => file.fileId !== fileId);
                        setMediaFiles(newMediaFilesArray);
                        onUpsertFeatureAnnouncementContent();
                    } else {
                        const returnValue = await fetchResult.json();
                        uiStore.printStatusMessage(returnValue.message, MessageType.ERROR);
                    }
                } catch (fetchError) {
                    uiStore.printStatusMessage(fetchError.error, MessageType.ERROR);
                }
            }
        },
        [
            authStore.token,
            authStore.tokenType,
            authStore.user,
            uiStore,
            mediaFiles,
            watchLanguage,
            onUpsertFeatureAnnouncementContent
        ]
    );

    const onDeleteAnnouncementContent = useCallback(async () => {
        for (const mediaFile of mediaFiles) {
            await onDeleteMedia(mediaFile.fileId)();
        }

        await deleteAnnouncementMlsMutation({
            variables: {
                announcementmlsid: contentId
            }
        });

        uiStore.setHasNewAnnouncementContentData(true);
        history.replace(selectRoute(Route.featureAnnouncementDetails, null, { announcementid: announcementId }));
    }, [contentId, deleteAnnouncementMlsMutation, history, uiStore, mediaFiles, onDeleteMedia, announcementId]);

    const announcementContentDeleteDialog = (
        <UI.ConfirmationDialog
            key="announcementMlsDeleteDialog"
            buttonText="Löschen"
            modalTitle="Content löschen"
            confirmationQuestion="Willst du den Content wirklich löschen?"
            inProgressText="Content wird gelöscht..."
            onConfirmation={onDeleteAnnouncementContent}
            buttonClasses={`${styles.FormButton} ${styles.Delete} customer-button ml-sm-2 mt-2 mt-sm-0`}
            isDangerousOperation
        />
    );

    const featureAnnouncementContentForm = (
        <UI.Form>
            <UI.Input
                label={"Announcement Content ID (announcements_mls)"}
                ref={registerRef}
                name="id"
                type="text"
                readOnly={true}
                disabled={userRole === CustomerRole.CUSTOMERADMIN}
            />
            <UI.Input
                label={"Sprache*"}
                ref={register({ required: true })}
                name="language"
                as="select"
                errorMsg={errors.language ? "Pflichtfeld!" : undefined}
                className={dirtyFields.language ? styles.changed : ""}
                disabled={!isNewFeatureAnnouncementContent || userRole === CustomerRole.CUSTOMERADMIN}
                defaultValue={DEFAULT_ANNOUNCEMENT_LANGUAGE}
            >
                <option value={"de"}>de</option>
                <option value={"fr"}>fr</option>
                <option value={"it"}>it</option>
                <option value={"en"}>en</option>
            </UI.Input>
            <UI.Input
                label={"Teasertext*"}
                ref={register({ required: true })}
                name="teaser"
                errorMsg={errors.teaser ? "Pflichtfeld!" : undefined}
                className={dirtyFields.teaser ? styles.changed : ""}
                type="text"
                as="textarea"
                disabled={userRole === CustomerRole.CUSTOMERADMIN}
            />
            <UI.Input
                label={"Titel*"}
                ref={register({ required: true })}
                name="title"
                errorMsg={errors.title ? "Pflichtfeld!" : undefined}
                className={dirtyFields.title ? styles.changed : ""}
                type="text"
                disabled={userRole === CustomerRole.CUSTOMERADMIN}
            />
            <UI.Input
                label={"Untertitel*"}
                ref={register({ required: true })}
                name="subtitle"
                errorMsg={errors.subtitle ? "Pflichtfeld!" : undefined}
                className={dirtyFields.subtitle ? styles.changed : ""}
                type="text"
                disabled={userRole === CustomerRole.CUSTOMERADMIN}
            />
            <UI.Input
                label={"Text und Medien"}
                ref={register({ required: true })}
                name="content"
                errorMsg={errors.content ? "Pflichtfeld!" : undefined}
                className={`${styles.ContentTextArea} ${dirtyFields.content ? styles.changed : ""}`}
                type="text"
                disabled={userRole === CustomerRole.CUSTOMERADMIN}
                as="textarea"
                placeholder={CONTENT_TEXT_PLACEHOLDER}
            />
            <UI.Button
                type="submit"
                label={currentStatus === FORM_STATUS.SAVING_FORM ? "Speichert..." : "Speichern"}
                disabled={!formState.isDirty || currentStatus === FORM_STATUS.SAVING_FORM}
                onClick={handleSubmit(handleFormSubmit)}
                className={`${styles.FormButton} customer-button`}
                ref={refSubmitButtom}
            />
            <UI.Button
                label={"Zurücksetzen"}
                disabled={!formState.isDirty || currentStatus === FORM_STATUS.SAVING_FORM}
                onClick={resetForm}
                className={`${styles.FormButton} customer-button ml-sm-2 mt-2 mt-sm-0`}
            />
            {!isNewFeatureAnnouncementContent ? announcementContentDeleteDialog : undefined}
            {props.children}
        </UI.Form>
    );

    const onClickUploadMedia = useCallback((e: any) => {
        e.preventDefault();
        hiddenMediaInputButton?.current?.click();
    }, []);

    const onHandleChangeHiddenInput = useCallback(
        async (event: FileHTMLInputEvent) => {
            setIsUploadingMediaFile(true);

            if (event?.target?.files) {
                const files = Array.from(event.target.files);

                if (files.length > 0) {
                    const file: File = files[0];

                    if (authStore.user && authStore.token) {
                        const accessToken = authStore.token;
                        const tokenType = authStore.tokenType;
                        const role = authStore.user?.role;

                        try {
                            let formData = new FormData();
                            formData.append(file.name, file);

                            const fetchResult = await fetch(
                                `${NetworkConfig.uploadAnnouncementFileUrl}/${announcementId}?lang=${watchLanguage}`,
                                {
                                    method: "POST",
                                    body: formData,
                                    headers: {
                                        "Content-type": "multipart/form-data",
                                        Authorization: `${tokenType} ${accessToken}`,
                                        "x-hasura-role": getRoleKey(role)
                                    }
                                }
                            );

                            if (fetchResult.status === 200) {
                                try {
                                    const result = await fetchResult.json();

                                    setMediaFiles((oldMediaFiles) => {
                                        const fileId = result.data.FileId;
                                        const fileName = result.data.FileName;
                                        const fileExtension = result.data.FileExtension;

                                        return [
                                            ...oldMediaFiles,
                                            {
                                                fileId,
                                                fileName,
                                                fileExtension,
                                                fileUrl: `https://${getEnvironment()}.wwportal.ch/files/announcements/${announcementId}/${watchLanguage}/${fileId}.${fileExtension}`
                                            }
                                        ];
                                    });

                                    onUpsertFeatureAnnouncementContent();
                                } catch {
                                    setIsUploadingMediaFile(false);
                                }

                                uiStore.printStatusMessage("Datei erfolgreich hochgeladen", MessageType.INFO);
                            } else {
                                const returnValue = await fetchResult.json();
                                uiStore.printStatusMessage(returnValue.message, MessageType.ERROR);
                            }
                        } catch (fetchError) {
                            uiStore.printStatusMessage(fetchError.error, MessageType.ERROR);
                        } finally {
                            setIsUploadingMediaFile(false);
                        }
                    }
                }
            }
        },
        [
            announcementId,
            authStore.token,
            authStore.tokenType,
            authStore.user,
            uiStore,
            watchLanguage,
            onUpsertFeatureAnnouncementContent
        ]
    );

    const renderMediaInformation = (mediaFile: MediaFile, index: number) => {
        return (
            <div key={mediaFile.fileId}>
                {index > 0 ? <hr className={styles.ImageDivider} /> : undefined}

                {isImage(mediaFile.fileExtension) ? (
                    <div>
                        <img className="w-100" src={mediaFile.fileUrl} alt="Media-Preview" />
                    </div>
                ) : undefined}

                <div className="my-2">FileId: {mediaFile.fileId}</div>
                <div className="my-2">FileName: {mediaFile.fileName}</div>
                <div className="d-flex">
                    <div className="d-flex align-items-center mr-4">
                        <UI.Icon
                            onClick={onCopyMedia(mediaFile.fileId, mediaFile.fileExtension)}
                            icon={UI.SVGIcon.Documents}
                            color={ColorStyle("black")}
                        />
                        <div>Kopieren</div>
                    </div>
                    <div className="d-flex align-items-center">
                        <UI.Icon
                            onClick={onDeleteMedia(mediaFile.fileId)}
                            icon={UI.SVGIcon.CloseCircle}
                            color={ColorStyle("black")}
                        />
                        <div>Löschen</div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <React.Fragment>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col md={9}>
                        {userRole ? (
                            userRole === CustomerRole.CUSTOMERDEV || userRole === CustomerRole.UCCMADMIN ? (
                                <UI.Card>
                                    {saveFormModal}
                                    {featureAnnouncementContentForm}
                                </UI.Card>
                            ) : undefined
                        ) : undefined}
                    </UI.Col>
                    <UI.Col md={3}>
                        <div className={`${styles.MediaContainer} p-3`}>
                            <div className="mb-2">Zusätzliche Aktionen</div>
                            <UI.Button
                                label={isUploadingMediaFile ? "Wird hochgeladen" : "Medien hochladen"}
                                disabled={isUploadingMediaFile}
                                onClick={onClickUploadMedia}
                                className="customer-button w-100"
                            />
                            <input
                                ref={hiddenMediaInputButton}
                                type="file"
                                className="d-none"
                                onChange={onHandleChangeHiddenInput}
                            />
                        </div>
                        <div className={`${styles.MediaContainer} mt-3 p-3`}>
                            <div className="mb-2">Media</div>
                            <div>{mediaFiles.map(renderMediaInformation)}</div>
                        </div>
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </React.Fragment>
    );
};

export const FeatureAnnouncementContentDetailsForm = Sentry.withProfiler(
    observer(FeatureAnnouncementContentDetailsFormBase)
);
